import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import CourseItem from '../livestreamEvent/CourseItem';
import LivestreamEvent from '../livestreamEvent/LivestreamEvent';
import TeamItem from '../team/TeamItem';
import ArticleItem from '../article/ArticleGridItem';
import ArticleGridBlock from '../article/ArticleGridBlock';
import PressResource from '../press/PressResource';
import PressGrid from '../press/PressGrid';
import CollapsibleList from '../collapsibleBlock/CollapsibleList';
import PlansSection from '../plans/PlansSection';
import BadgesSection from '../badges/BadgesSection';
import LabelsSection from '../labels/LabelsSection';
import DownloadApp from '../download/DownloadApp';
import ReviewsSection from '../reviews/ReviewsSection';
import Header from '../header/Header';
import SimpleHeader from '../../chrome/Header';
import Footer from '../footer/Footer';
import ThreeColumnsBlock from '../columnBlock/ThreeColumnsBlock';
import TwoColumnsBlock from '../columnBlock/TwoColumnsBlock';
import OneColumnBlock from '../columnBlock/OneColumnBlock';
import HeroBlockCarousel from '../heroBlock/HeroBlockCarousel';
import HeroBlock from '../heroBlock/HeroBlock';
import Hero from '../heroBlock/Hero';
import ThreeColumnsHeroBlock from '../heroBlock/ThreeColumnsHeroBlock';
import VideoBlock from '../videoBlock/VideoBlock';
import DiscoverCarousel from '../carousel/DiscoverCarousel';
import JoinWidget from '../careers/JoinWidget';
import VerticalLine from '../utils/VerticalLine';
import MultiColumnsGridBlock from '../gridBlock/MultiColumnsGridBlock';
import TextBlockWithIcon from '../textBlock/TextBlockWithIcon';
import AlbumOrPlaylist from '../audio/AlbumOrPlaylist';
import { CardItem, HeroItem, TextBlockItem } from './CustomItem';
import Iframe from '../embed/Iframe.js';
import GiftCard from '../giftCard/GiftCard';

export const getArtworkUrl = artwork => {
  const url = get(artwork, 'fields.file.url');
  if (!url) {
    return null;
  }
  return new URL(url, process.env.BASE_URL).toString();
};

export const getArtworkDimensions = artwork => {
  const { width, height } = get(artwork, 'fields.file.details.image') || {};
  if (!width || !height) {
    return {};
  }
  return { width, height };
};

export const BLOCK_TYPE = {
  PRESS_RESOURCES: 'pressResources',
  COLLAPSIBLE_ITEM: 'landingPagesCollapsible',
  TEAM_MEMBER: 'teamMember',
  LIVESTREAM_EVENT: 'livestreamEvent',
  ALBUM: 'livesteamAlbum',
  PLAYLIST: 'livestreamPlaylist',
  MAGAZINE_ARTICLE: 'magazineArticle',
  ONE_COLUMN_BLOCK: 'landingPagesOneColumnBlock',
  HERO_BLOCK: 'landingPagesHeroBlock',
  COMMON_BLOCK: 'landingPagesCommonBlock',
  HERO_BLOCK_CAROUSEL: 'landingPagesHeroBlockCollection',
  TWO_COLUMNS_BLOCK: 'landingPagesTwoColumnBlock',
  THREE_COLUMNS_BLOCK: 'landingPagesThreeColumnBlock',
  VIDEO_BLOCK: 'landingPagesVideoBlock',
  MULTI_COLUMNS_GRID_BLOCK: 'landingPagesThreeColumnsGridBlock',
  THREE_COLUMNS_HERO_BLOCK: 'landingPagesThreeColumnHeroBlock',
  COLLAPSIBLE_LIST: 'landingPagesCollapsibleList',
  DISCOVER_CAROUSEL: 'livestreamEventCollection',
  MAIN_PAGE: 'landingPagesPage',
  HEADER: 'Header',
  SIMPLE_HEADER: 'Simple Header',
  PLANS: 'Plans',
  BADGES: 'Badges',
  LABELS: 'Labels',
  FOOTER: 'Footer',
  VERTICAL_LINE: 'Vertical Line',
  PERSONIO: 'Personio',
  PRESS_RELEASES: 'Press Releases',
  PRESS_MENTIONS: 'Press Mentions',
  DOWNLOAD_APP: 'Download App',
  REVIEWS: 'Reviews',
  IFRAME: 'iframe',
  GIFT_UP: 'giftUp',
};

const makeComponent = (Component, props) => <Component key={props.id} {...props} />;

const CommonBlockComponents = {
  [BLOCK_TYPE.HEADER]: Header,
  [BLOCK_TYPE.SIMPLE_HEADER]: SimpleHeader,
  [BLOCK_TYPE.PLANS]: PlansSection,
  [BLOCK_TYPE.BADGES]: BadgesSection,
  [BLOCK_TYPE.LABELS]: LabelsSection,
  [BLOCK_TYPE.FOOTER]: Footer,
  [BLOCK_TYPE.VERTICAL_LINE]: VerticalLine,
  [BLOCK_TYPE.PERSONIO]: JoinWidget,
  [BLOCK_TYPE.PRESS_RELEASES]: PressGrid,
  [BLOCK_TYPE.PRESS_MENTIONS]: PressGrid,
  [BLOCK_TYPE.DOWNLOAD_APP]: DownloadApp,
  [BLOCK_TYPE.REVIEWS]: ReviewsSection,
};

const getCommonBlock = ({ id, type, content, ...props }) => {
  const Component = CommonBlockComponents[type];
  if (!Component) return null;

  const specificProps = {
    id,
    content,
    type,
    ...(type === BLOCK_TYPE.PRESS_RELEASES && {
      content: props.pressReleases,
      type,
    }),
    ...(type === BLOCK_TYPE.PRESS_MENTIONS && {
      content: props.pressMentions,
      type,
    }),
    ...props,
  };

  return makeComponent(Component, specificProps);
};

const getGridBlock = ({ id, content }) => {
  const hasArticles =
    content.blocks &&
    content.blocks.some(
      block => get(block, 'sys.contentType.sys.id') === BLOCK_TYPE.MAGAZINE_ARTICLE
    );
  const GridComponent = hasArticles ? ArticleGridBlock : MultiColumnsGridBlock;
  return makeComponent(GridComponent, { id, content });
};

export const blocksToComponents = ({ block, src: srcBlock, ...props }) => {
  const id = block.sys.id;
  const content = block.fields;
  if (!content) {
    return null;
  }

  const componentMap = {
    [BLOCK_TYPE.MAIN_PAGE]: {
      [BLOCK_TYPE.COMMON_BLOCK]: getCommonBlock({
        id,
        type: content.type,
        content,
        ...props,
      }),
      [BLOCK_TYPE.HERO_BLOCK_CAROUSEL]: makeComponent(HeroBlockCarousel, {
        id,
        content,
      }),
      [BLOCK_TYPE.HERO_BLOCK]: makeComponent(HeroBlock, { id, content }),
      [BLOCK_TYPE.TWO_COLUMNS_BLOCK]: makeComponent(TwoColumnsBlock, {
        id,
        content,
      }),
      [BLOCK_TYPE.ONE_COLUMN_BLOCK]: makeComponent(OneColumnBlock, {
        id,
        content,
      }),
      [BLOCK_TYPE.THREE_COLUMNS_BLOCK]: makeComponent(ThreeColumnsBlock, {
        id,
        content,
      }),
      [BLOCK_TYPE.VIDEO_BLOCK]: makeComponent(VideoBlock, { id, content }),
      [BLOCK_TYPE.MULTI_COLUMNS_GRID_BLOCK]: getGridBlock({ id, content }),
      [BLOCK_TYPE.THREE_COLUMNS_HERO_BLOCK]: makeComponent(ThreeColumnsHeroBlock, { id, content }),
      [BLOCK_TYPE.COLLAPSIBLE_LIST]: makeComponent(CollapsibleList, { id, content }),
      [BLOCK_TYPE.DISCOVER_CAROUSEL]: makeComponent(DiscoverCarousel, { id, content }),
      [BLOCK_TYPE.IFRAME]: makeComponent(Iframe, { id, content }),
      [BLOCK_TYPE.GIFT_UP]: makeComponent(GiftCard, { id, content }),
    },
    [BLOCK_TYPE.DISCOVER_CAROUSEL]: {
      [BLOCK_TYPE.LIVESTREAM_EVENT]: makeComponent(LivestreamEvent, {
        id,
        content,
      }),
      [BLOCK_TYPE.ALBUM]: makeComponent(AlbumOrPlaylist, {
        id,
        content,
        type: BLOCK_TYPE.ALBUM,
      }),
      [BLOCK_TYPE.PLAYLIST]: makeComponent(AlbumOrPlaylist, {
        id,
        content,
        type: BLOCK_TYPE.PLAYLIST,
      }),
      [BLOCK_TYPE.ONE_COLUMN_BLOCK]: (
        <CardItem key={id} content={content}>
          {makeComponent(TextBlockWithIcon, {
            content,
            srcBlockName: `Discover Carousel - ${content.title}`,
          })}
        </CardItem>
      ),
    },
    [BLOCK_TYPE.COLLAPSIBLE_ITEM]: {
      [BLOCK_TYPE.PRESS_RESOURCES]: makeComponent(PressResource, {
        id,
        content,
      }),
      [BLOCK_TYPE.ONE_COLUMN_BLOCK]: makeComponent(TextBlockWithIcon, {
        id,
        content,
        srcBlockName: `Collapsible Item - ${content.title}`,
      }),
    },
    [BLOCK_TYPE.MULTI_COLUMNS_GRID_BLOCK]: {
      [BLOCK_TYPE.TEAM_MEMBER]: makeComponent(TeamItem, {
        id,
        content,
        openModal: props.openModal,
      }),
      [BLOCK_TYPE.LIVESTREAM_EVENT]: makeComponent(CourseItem, { id, content }),
      [BLOCK_TYPE.MAGAZINE_ARTICLE]: makeComponent(ArticleItem, { id, content }),
      [BLOCK_TYPE.ONE_COLUMN_BLOCK]: (
        <TextBlockItem key={id} content={content}>
          {makeComponent(TextBlockWithIcon, {
            content,
            srcBlockName: `Multi Columns Grid Block Item - ${content.title}`,
          })}
        </TextBlockItem>
      ),
    },
    [BLOCK_TYPE.THREE_COLUMNS_BLOCK]: {
      [BLOCK_TYPE.ONE_COLUMN_BLOCK]: (
        <TextBlockItem key={id} content={content}>
          {makeComponent(TextBlockWithIcon, {
            content,
            srcBlockName: `Three Columns Block Item - ${content.title}`,
          })}
        </TextBlockItem>
      ),
    },
    [BLOCK_TYPE.THREE_COLUMNS_HERO_BLOCK]: {
      [BLOCK_TYPE.HERO_BLOCK]: (
        <HeroItem key={id} content={content}>
          {makeComponent(Hero, { content, centerText: true, height: '500px' })}
        </HeroItem>
      ),
    },
  };

  const targetBlock = block.sys.contentType.sys.id;
  return componentMap[srcBlock][targetBlock] || null;
};

blocksToComponents.propTypes = {
  block: PropTypes.object.isRequired,
  src: PropTypes.string.isRequired,
};



// WEBPACK FOOTER //
// /tmp/build_83f21ab6/src/components/main/utils/Contentful.js